/**
 * Pure, domain-free string-similarity primitives for the offline matcher.
 *
 * These know nothing about CRM fields or CSVs — they compare strings. The
 * matching tiers in layer-fuzzy compose them; keeping them here makes each one
 * independently testable and the tier logic small.
 */

// Absolute distance ≤2 mis-fires on short tokens: on 3-char strings it means two
// of three characters differ (org→url, tel→url both score 2) yet still passes.
// Scale the bound to the header length instead. Short tokens allow only a single
// edit (a real one-typo case like "nam"→"name"); longer ones can afford 2
// (transpositions such as "nmae"→"name" cost 2 in plain Levenshtein). Two edits
// is the practical ceiling. Thresholds are injected (from `matchingConfig`) so
// this module stays a pure, engine-agnostic primitive.
export function maxFuzzyDistance(
  length: number,
  shortLength: number,
  shortMax: number,
  longMax: number,
): number {
  return length <= shortLength ? shortMax : longMax
}

/** Lowercase, strip diacritics, collapse separators/whitespace to single spaces. */
export function normalise(value: string): string {
  return (
    value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // strip diacritics (combining marks)
      // Polish ł / German ß don't decompose under NFD (they're distinct letters,
      // not base + combining mark), so map them explicitly — otherwise a header
      // typed "płeć"/"straße" wouldn't match an ASCII synonym "plec"/"strasse".
      .replace(/ł/gi, 'l')
      .replace(/ß/gi, 'ss')
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
  )
}

/** Classic two-row Levenshtein distance. Zero dependencies. */
export function levenshtein(a: string, b: string): number {
  if (a === b) {
    return 0
  }
  if (a.length === 0) {
    return b.length
  }
  if (b.length === 0) {
    return a.length
  }

  // Single-row algorithm. `row[j]` is the distance for b[0..j]; `diag` holds the
  // previous row's value at j-1 (the substitution cost source).
  const row = Array.from({ length: b.length + 1 }, (_, i) => i)

  for (let i = 0; i < a.length; i++) {
    let diag = row[0] ?? 0 // prev row, col 0
    row[0] = i + 1
    for (let j = 0; j < b.length; j++) {
      const prevDiag = diag
      diag = row[j + 1] ?? 0 // prev row, col j+1 — becomes next diag
      const cost = a[i] === b[j] ? 0 : 1
      row[j + 1] = Math.min(
        (row[j] ?? 0) + 1, // insertion
        diag + 1, // deletion
        prevDiag + cost, // substitution
      )
    }
  }

  return row[b.length] ?? 0
}

/**
 * True when `abbr` is an in-order subsequence of `full` sharing its first letter,
 * with no gap wider than `maxGap` between matched characters — the signal that
 * separates a real abbreviation from a coincidental subsequence. A genuine
 * abbreviation drops short internal runs ("email"→"eml" skips 2 chars); it does
 * not leap across half the word ("civility"→"city" skips 4). So "eml"→"email"
 * holds; "org" is NOT a subsequence of "url" (distance-2 false positive
 * rejected); "city" leaps too far inside "civility" and is rejected too.
 * Requires at least two characters and a genuinely shorter header so single
 * letters and equal-length near-misses don't slip through. `maxGap` is injected
 * (from `matchingConfig`) to keep this module engine-agnostic.
 */
export function isAbbreviation(
  abbr: string,
  full: string,
  maxGap: number,
): boolean {
  if (abbr.length < 2 || abbr.length >= full.length || abbr[0] !== full[0]) {
    return false
  }
  let i = 0
  let lastMatch = -1
  for (let pos = 0; pos < full.length; pos++) {
    if (full[pos] === abbr[i]) {
      if (lastMatch >= 0 && pos - lastMatch - 1 > maxGap) {
        return false // jumped across too many characters — not an abbreviation
      }
      lastMatch = pos
      i++
      if (i === abbr.length) {
        return true
      }
    }
  }
  return false
}
